import { useState } from 'react';
import { buildFoldCardPdf, filenameForFoldCard } from '../lib/pdf';
import { timestampForFilename, triggerDownload } from '../lib/canvas';
import { FeedbackPrompt, type FeedbackPayload } from './FeedbackPrompt';
import type { AppState } from '../hooks/useAppState';

interface Props {
  app: AppState;
  variant?: 'full' | 'compact';
}

const SNOOZE_KEY = 'fcp-feedback-snooze';

// Local-time YYYY-MM-DD. Snooze granularity is "calendar day in user's tz."
const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const isSnoozedToday = () => {
  try {
    return window.localStorage.getItem(SNOOZE_KEY) === todayKey();
  } catch {
    return false;
  }
};

const snoozeUntilTomorrow = () => {
  try {
    window.localStorage.setItem(SNOOZE_KEY, todayKey());
  } catch {
    /* localStorage unavailable */
  }
};

// Fire-and-forget anonymous opt-in ping. Errors swallowed deliberately: in
// dev the /api/feedback Pages Function isn't running, so failures are expected.
// `keepalive: true` lets the request complete even if the page is closed.
const sendFeedback = (payload: FeedbackPayload) => {
  try {
    void fetch('/api/feedback', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...payload, ts: Date.now() }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* fetch unavailable */
  }
};

export function MainDownloadButton({ app, variant = 'full' }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const download = async () => {
    setErr(null);
    setBusy(true);
    try {
      const bytes = await buildFoldCardPdf({
        slots: app.slots,
        cardSize: app.cardSize,
        paperSize: app.paperSize,
        orientation: app.orientation,
        drift: app.drift,
        marks: app.marks,
        printer: app.printer,
      });
      triggerDownload(
        bytes,
        filenameForFoldCard(app.cardSize, app.orientation, timestampForFilename())
      );
      // Show the prompt on every successful download UNLESS the user explicitly
      // snoozed it for today. Submitting or closing without snoozing will still
      // bring the prompt back on the next download — only "Don't ask today" mutes it.
      if (!isSnoozedToday()) {
        setShowFeedback(true);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (payload: FeedbackPayload) => {
    sendFeedback(payload);
    setShowFeedback(false);
  };

  const handleDismiss = () => {
    // Close this instance only — DON'T snooze. The next download will reopen
    // the prompt. This matches the "show every download" behavior the user
    // wants while still letting them tap-to-close any individual instance.
    setShowFeedback(false);
  };

  const handleSnoozeToday = () => {
    snoozeUntilTomorrow();
    setShowFeedback(false);
  };

  return (
    <>
      <div className={`main-dl main-dl-${variant}`}>
        <button
          type="button"
          className="primary-btn main-dl-btn"
          onClick={download}
          disabled={app.downloadDisabled || busy}
        >
          {busy ? 'Building PDF…' : '⬇  Download printable PDF'}
        </button>
        {variant === 'full' && (
          <p className="main-dl-meta">
            {app.cardSize.label} card · {app.paperSize.label} paper · {app.hasInside ? '2 pages' : '1 page'} + instructions
          </p>
        )}
        {app.downloadDisabled && app.downloadDisabledReason && (
          <p className="actions-hint warn">{app.downloadDisabledReason}</p>
        )}
        {err && <p className="actions-hint err">{err}</p>}
      </div>

      {showFeedback && (
        <FeedbackPrompt
          onSubmit={handleSubmit}
          onDismiss={handleDismiss}
          onSnoozeToday={handleSnoozeToday}
        />
      )}
    </>
  );
}
